import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, API, apiError } from "@/lib/api";
import { formatBytes } from "@/content/resourceCatalog";

const FIELDS = [
  { name: "name", label: "Full name", type: "text", required: true, placeholder: "Priya Sharma" },
  { name: "email", label: "Work email", type: "email", required: true, placeholder: "you@company.com" },
  { name: "company", label: "Company", type: "text", placeholder: "Company name" },
  { name: "phone", label: "Phone (optional)", type: "tel", placeholder: "+91 00000 00000" },
];

export const DownloadDialog = ({ slot, file, open, onClose }) => {
  const [form, setForm] = React.useState({ name: "", email: "", company: "", phone: "" });
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setDone(false);
      setBusy(false);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/resources/${slot.slotKey}/request`, form);
      const link = document.createElement("a");
      link.href = `${API}/resources/${slot.slotKey}/file?token=${data.token}`;
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setDone(true);
      toast.success("Your download has started.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="download-dialog"
        >
          <div className="absolute inset-0 bg-[#05070D]/85 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-[#8E9AEA]/25 bg-[#0D1322] p-5 sm:p-7 shadow-[0_40px_120px_-30px_rgba(142,154,234,0.35)] sm:p-9"
          >
            <button
              onClick={onClose}
              aria-label="Close"
              className="absolute right-5 top-5 text-[#A6B0C9] transition-colors duration-200 hover:text-white"
              data-testid="download-dialog-close"
            >
              <X className="h-5 w-5" />
            </button>

            {done ? (
              <div className="py-6 text-center" data-testid="download-dialog-success">
                <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
                <h3 className="mt-5 font-display text-2xl font-bold text-white">Download started</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-[#A6B0C9] sm:text-sm">
                  If it didn't open automatically, check your browser downloads. A Lanvaus engineer can walk you
                  through anything in the document.
                </p>
                <button onClick={onClose} className="btn-secondary mt-7" data-testid="download-dialog-done">
                  Close
                </button>
              </div>
            ) : (
              <>
                <span className="pill-badge">
                  <ShieldCheck className="h-3.5 w-3.5 text-[#8E9AEA]" />
                  {slot.badge}
                </span>
                <h3 className="mt-5 font-display text-xl font-bold leading-snug text-white sm:text-2xl">
                  {slot.title}
                </h3>
                <p className="mt-2 text-xs text-[#A6B0C9]">
                  PDF · {formatBytes(file?.size)} · {slot.serviceLabel}
                </p>

                <form onSubmit={submit} className="mt-7 grid gap-4 sm:grid-cols-2">
                  {FIELDS.map((f) => (
                    <label key={f.name} className={f.name === "name" || f.name === "email" ? "sm:col-span-2" : ""}>
                      <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#A6B0C9]">
                        {f.label}
                      </span>
                      <input
                        type={f.type}
                        required={f.required}
                        placeholder={f.placeholder}
                        value={form[f.name]}
                        onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                        className="mt-2 w-full rounded-lg border border-white/12 bg-[#0B0F1A] px-4 py-3 text-sm text-white outline-none transition-colors duration-200 placeholder:text-[#7C86A2] focus:border-[#8E9AEA]/60"
                        data-testid={`download-field-${f.name}`}
                      />
                    </label>
                  ))}
                  <button
                    type="submit"
                    disabled={busy}
                    className="btn-primary sm:col-span-2 disabled:opacity-60"
                    data-testid="download-dialog-submit"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    {busy ? "Preparing your file…" : "Download the PDF"}
                  </button>
                </form>
                <p className="mt-4 text-[11px] leading-relaxed text-[#7C86A2]">
                  We use your details only to send the document and relevant security guidance. No spam, unsubscribe
                  anytime.
                </p>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
